<i18n lang="yaml">
en:
  title: 'Fixed Configurations'
  subtitle: 'Optimal balance of&nbsp;processor cores, memory, and disks. This allows you to&nbsp;use more resources than in&nbsp;flexible configurations.'
  create: 'Create'
  gb: 'GB'
  description1: 'Balanced configurations to&nbsp;suit any task.'
  description2: 'Configurations for workloads requiring high-performance and high-speed CPU computing.'
  description3: 'Configurations for running applications that require lots of&nbsp;RAM.'
  price: 'Price'
  perHour: 'per hour'
  perMonth: 'per month'
ru:
  title: 'Фиксированные конфигурации'
  subtitle: 'Подобраны по&nbsp;оптимальному соотношению ядер процессора, памяти и&nbsp;дисков. Можно использовать большее количество ресурсов, чем в&nbsp;гибких конфигурациях.'
  create: 'Создать'
  gb: 'ГБ'
  description1: 'Сбалансированные конфигурации, подходящие для любых задач.'
  description2: 'Конфигурации для нагрузок, требующих производительности и скорости вычислений на CPU.'
  description3: 'Конфигурации для работы приложений, требующих большой объем памяти.'
  price: 'Цена'
  perHour: 'за час'
  perMonth: 'за месяц'
</i18n>

<template>
  <s-section
    :title="$t('title')"
    :subtitle="$t('subtitle')"
  >
    <s-preloader v-if="isLoading"></s-preloader>
    <div
      v-else
      class="cloud-fixed-conf"
    >
      <div class="cloud-fixed-conf-filter">
        <div
          v-show="!$nuxt.$breakpoints.sSm"
          class="cloud-fixed-conf-filter__tabs"
        >
          <label class="cloud-fixed-conf-filter__tab">
            <input
              v-model="currentTariffLine"
              class="cloud-fixed-conf-filter__tab-field"
              type="radio"
              value="Standard Line"
              name="tariff"
            >
            <span class="cloud-fixed-conf-filter__tab-name">Standard Line</span>
          </label>
          <label class="cloud-fixed-conf-filter__tab">
            <input
              v-model="currentTariffLine"
              class="cloud-fixed-conf-filter__tab-field"
              type="radio"
              value="CPU Line"
              name="tariff"
            >
            <span class="cloud-fixed-conf-filter__tab-name">CPU Line</span>
          </label>
          <label class="cloud-fixed-conf-filter__tab">
            <input
              v-model="currentTariffLine"
              class="cloud-fixed-conf-filter__tab-field"
              type="radio"
              value="Memory Line"
              name="tariff"
            >
            <span class="cloud-fixed-conf-filter__tab-name">Memory Line</span>
          </label>
        </div>
        <a-select
          v-show="$nuxt.$breakpoints.sSm"
          v-model="currentTariffLine"
          class="cloud-fixed-conf-filter__select"
        >
          <a-select-option value="Standard Line">
            Standard Line
          </a-select-option>
          <a-select-option value="CPU Line">
            CPU Line
          </a-select-option>
          <a-select-option value="Memory Line">
            Memory Line
          </a-select-option>
        </a-select>

        <div class="cloud-fixed-conf-filter__period">
          <span
            v-show="$nuxt.$breakpoints.sSm"
            class="cloud-fixed-conf-filter__period-title"
          >
            {{ $t('price') }}, ₽
          </span>
          <a-radio-group v-model="currentPeriod">
            <a-radio-button value="hour">
              <template v-show="!$nuxt.$breakpoints.sSm">
                {{ $t('price') }}
              </template>
              {{ $t('perHour') }}
            </a-radio-button>
            <a-radio-button value="month">
              <template v-show="!$nuxt.$breakpoints.sSm">
                {{ $t('price') }}
              </template>
              {{ $t('perMonth') }}
            </a-radio-button>
          </a-radio-group>
        </div>
      </div>
      <div class="cloud-fixed-conf__notice row">
        <div class="col-xl-9 ml-auto">
          <p
            v-if="currentTariffLine === TariffLine.STANDARD"
            v-html="$t('description1')"
          ></p>
          <p
            v-if="currentTariffLine === TariffLine.CPU"
            v-html="$t('description2')"
          ></p>
          <p
            v-if="currentTariffLine === TariffLine.MEMORY"
            v-html="$t('description3')"
          ></p>
        </div>
      </div>
      <div class="cloud-fixed-conf__inner">
        <div class="cloud-fixed-conf__list">
          <div
            v-for="configuration in configurationsSource"
            :key="configuration.vCpuQuantity"
            class="cloud-fixed-conf__item"
          >
            <div class="cloud-fixed-conf__item-row">
              <span class="cloud-fixed-conf__item-row-title">vCPU</span>
              <span class="cloud-fixed-conf__item-row-value">
                {{ configuration.vCpuQuantity }}
              </span>
            </div>
            <div class="cloud-fixed-conf__item-row">
              <span class="cloud-fixed-conf__item-row-title">RAM</span>
              <span class="cloud-fixed-conf__item-row-value">
                {{ configuration.ramSize }} {{ $t('gb') }}
              </span>
            </div>
            <div class="cloud-fixed-conf__item-row">
              <span class="cloud-fixed-conf__item-row-title">SSD</span>
              <span class="cloud-fixed-conf__item-row-value">
                {{ configuration.ssdSize }} {{ $t('gb') }}
              </span>
            </div>
            <div class="cloud-fixed-conf__item-cost">
              {{ $priceFormatter(getTotalPrice(configuration)) }} / {{ periodToName[currentPeriod] }}
            </div>
            <a
              class="cloud-fixed-conf__item-btn btn btn--dark btn--wide"
              href="https://my.selectel.ru/registration?destination=/vpc/projects"
              rel="noopener noreferrer"
              target="_blank"
              v-html="$t('create')"
            ></a>
          </div>
        </div>
      </div>
    </div>

    <script
      v-if="hasMicrodata"
      type="application/ld+json"
      v-html="jsonProductMicrodata"
    ></script>
  </s-section>
</template>

<script>
import TimeMultiplier from '~/mixins/time-multiplier'
import Microdata from '~/mixins/seo/product-microdata'

import {DAYS_IN_MONTH, HOURS_IN_DAY, KOPECKS_IN_RUBLE} from '~/helpers/constants'

import {Resource} from '~/helpers/prices/vpc/enum'

import {Period} from '~/helpers/enum/period'
import {mapActions, mapGetters} from 'vuex'

const TariffLine = {
  STANDARD: 'Standard Line',
  CPU: 'CPU Line',
  MEMORY: 'Memory Line'
}

const periodToName = {
  en: {
    [Period.HOUR]: 'hour',
    [Period.DAY]: 'day',
    [Period.MONTH]: 'month'
  },
  ru: {
    [Period.HOUR]: 'час',
    [Period.DAY]: 'день',
    [Period.MONTH]: 'месяц'
  }
}

const configurations = [
  {
    tariffLine: TariffLine.STANDARD,
    vCpuQuantity: 1,
    ramSize: 1,
    ssdSize: 8
  },
  {
    tariffLine: TariffLine.STANDARD,
    vCpuQuantity: 4,
    ramSize: 16,
    ssdSize: 128
  },
  {
    tariffLine: TariffLine.STANDARD,
    vCpuQuantity: 8,
    ramSize: 32,
    ssdSize: 384
  },
  {
    tariffLine: TariffLine.STANDARD,
    vCpuQuantity: 24,
    ramSize: 96,
    ssdSize: 1000
  },
  {
    tariffLine: TariffLine.CPU,
    vCpuQuantity: 4,
    ramSize: 8,
    ssdSize: 128
  },
  {
    tariffLine: TariffLine.CPU,
    vCpuQuantity: 8,
    ramSize: 16,
    ssdSize: 192
  },
  {
    tariffLine: TariffLine.CPU,
    vCpuQuantity: 16,
    ramSize: 32,
    ssdSize: 384
  },
  {
    tariffLine: TariffLine.CPU,
    vCpuQuantity: 24,
    ramSize: 48,
    ssdSize: 512
  },
  {
    tariffLine: TariffLine.MEMORY,
    vCpuQuantity: 2,
    ramSize: 16,
    ssdSize: 64
  },
  {
    tariffLine: TariffLine.MEMORY,
    vCpuQuantity: 4,
    ramSize: 32,
    ssdSize: 128
  },
  {
    tariffLine: TariffLine.MEMORY,
    vCpuQuantity: 8,
    ramSize: 64,
    ssdSize: 256
  },
  {
    tariffLine: TariffLine.MEMORY,
    vCpuQuantity: 16,
    ramSize: 128,
    ssdSize: 512
  }
]

export default {
  name: 'CloudServersConfigurations',
  mixins: [TimeMultiplier, Microdata],
  async fetch() {
    if (this.getVpcCloudServersConfigurationsPrices.length) {
      this.prices = this.getVpcCloudServersConfigurationsPrices
    } else {
      await this.fetchVpcPrices()

      this.prices = this.getVpcCloudServersConfigurationsPrices
    }
    this.isLoading = false
  },
  fetchOnServer: true,
  data() {
    return {
      configurations,
      prices: [],
      isLoading: true,
      TariffLine,
      currentPeriod: Period.HOUR,
      currentTariffLine: TariffLine.STANDARD,
      periodToName: periodToName[this.$i18n.locale]
    }
  },
  computed: {
    configurationsSource() {
      return this.configurations.filter(configuration => configuration.tariffLine === this.currentTariffLine)
    },
    vCpuPrice() {
      const vCpuPrice = this.prices.find(price => price.resource === Resource.COMPUTE_CORES)

      return vCpuPrice
        ? vCpuPrice.value
        : 0
    },
    ramPrice() {
      const ramPrice = this.prices.find(price => price.resource === Resource.COMPUTE_RAM)

      return ramPrice
        ? ramPrice.value
        : 0
    },
    ssdPrice() {
      const ssdPrice = this.prices.find(price => price.resource === Resource.VOLUME_GIGABYTES_LOCAL)

      return ssdPrice
        ? ssdPrice.value
        : 0
    },
    microdataPrice() {
      return (this.getTotalPrice({vCpuQuantity: 1, ramSize: 1, ssdSize: 8}) * DAYS_IN_MONTH * HOURS_IN_DAY)
        .toFixed(2)
        .toString()
    },
    ...mapGetters('rest-client/prices/vpc', ['getVpcCloudServersConfigurationsPrices'])
  },
  methods: {
    getTotalPrice(configuration) {
      return (
        configuration.vCpuQuantity * this.vCpuPrice +
        configuration.ramSize * this.ramPrice +
        configuration.ssdSize * this.ssdPrice
      ) * this.timeMultiplier / KOPECKS_IN_RUBLE
    },
    ...mapActions('rest-client/prices/vpc', ['fetchVpcPrices'])
  }
}
</script>

<style lang="scss">
.cloud-fixed-conf-filter {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid $grayBorder;

  @media (max-width: $breakpoint-xl) {
    grid-template-columns: 60% 40%;
    column-gap: 0;
  }

  @media (max-width: $breakpoint-md) {
    grid-template-columns: repeat(1, 1fr);
    border: 0;
  }
}

.cloud-fixed-conf-filter__tabs {
  grid-column-start: 2;
  grid-column-end: 4;
  margin-bottom: 16px;
  line-height: 32px;

  @media (max-width: $breakpoint-xl) {
    grid-column-start: 1;
    grid-column-end: auto;
  }
}

.cloud-fixed-conf-filter__tab {
  position: relative;
  display: inline-block;
  margin-right: 20px;
  cursor: pointer;
}

.cloud-fixed-conf-filter__tab-field {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  visibility: hidden;

  &:checked ~ .cloud-fixed-conf-filter__tab-name {
    color: $secondary;

    &:before {
      background-color: $secondary;
    }
  }
}

.cloud-fixed-conf-filter__tab-name {
  position: relative;
  display: block;
  transition: color 0.2s;

  &:hover {
    color: $secondary;
  }

  &:before {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: transparent;
    transition: background-color 0.4s;
  }
}

.cloud-fixed-conf-filter__period {
  margin: 0 0 16px auto;

  @media (max-width: $breakpoint-md) {
    grid-row-start: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 24px;
  }
}

.cloud-fixed-conf-filter__period-title {
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.cloud-fixed-conf__notice {
  margin-bottom: 16px;

  @media (max-width: $breakpoint-md) {
    font-size: 16px;
  }
}

.cloud-fixed-conf__inner {
  @media (max-width: $breakpoint-xl) {
    overflow-y: hidden;
  }
}

.cloud-fixed-conf__list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 16px;

  @media (max-width: $breakpoint-lg) {
    grid-template-columns: repeat(4, 280px);
  }

  @media (max-width: $breakpoint-md) {
    grid-gap: 8px;
  }
}

.cloud-fixed-conf__item {
  padding: 16px;
  border: 1px solid $grayBorder;
  border-radius: 6px;
}

.cloud-fixed-conf__item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.cloud-fixed-conf__item-cost {
  margin-bottom: 16px;
  padding-top: 4px;
  color: $secondary;
}
</style>